
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Divider,
  InputAdornment,
  Slider,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Chip,
  Select,
  MenuItem,
  Container,
} from '@mui/material';
import {
  LockOutlined as LockIcon,
  Email as EmailIcon,
  CalendarMonth as CalendarIcon,
  GroupAdd as GroupAddIcon,
} from '@mui/icons-material';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { createPaymentIntent, fetchPaymentMethods, estimateTax } from '../api/paymentApi';
import usePaymentStore from '../../../store/paymentStore';
import { fetchUsageStats } from '../../account/api/accountApi';
import useAccountStore from '../../../store/accountStore';
import PaymentMethodsList from './PaymentMethodsList';
import SubscriptionManagement from '../../subscription/components/SubscriptionManagement';
import { brand } from '../../../config/brand';

// ============================================================================
// INVITATION MODE TOGGLE
// Set to false to enable credit card payments, true for invitation-only mode
// ============================================================================
const INVITATION_MODE_ENABLED = false;

// Load Stripe outside of component render cycle
const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLIC_KEY || 'pk_test_51Qoix3KoczsQOoB4YvsPWn5b0U6WPBfcht2GXDDbRRjilxgNbfaoQx12jiOeV1WXP3W2ny7wOpy25UUsv08SrFxJ00hwEI4tlJ');

// Pre-defined amounts for quick selection
const predefinedAmounts = [10, 25, 50, 100, 200, 500];

// Card element styles
const cardElementOptions = {
  style: {
    base: {
      fontSize: '16px',
      color: '#424770',
      '::placeholder': {
        color: '#aab7c4',
      },
    },
    invalid: {
      color: '#9e2146',
    },
  },
};

// Inner component with Stripe context
const PaymentFormContent: React.FC = () => {
  const stripe = useStripe();
  const elements = useElements();
  const { paymentMethods, isLoading: paymentLoading, error: storePaymentError } = usePaymentStore();
  const { usage } = useAccountStore();
  
  const [amount, setAmount] = useState<number>(25);
  const [customAmount, setCustomAmount] = useState<string>('25');
  const [useExistingCard, setUseExistingCard] = useState<boolean>(true);
  const [cardError, setCardError] = useState<string | null>(null);
  const [processing, setProcessing] = useState<boolean>(false);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [paymentSuccess, setPaymentSuccess] = useState<boolean>(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  // Tax estimation states
  const [taxEstimate, setTaxEstimate] = useState<number>(0);
  const [totalWithTax, setTotalWithTax] = useState<number>(25);
  const [taxType, setTaxType] = useState<string>('tax');
  const [taxRate, setTaxRate] = useState<number>(0);
  const [estimatingTax, setEstimatingTax] = useState<boolean>(false);
  const [selectedCountry, setSelectedCountry] = useState<string>('IL'); // Default to Israel

  // Fetch payment methods
  useEffect(() => {
    const loadData = async () => {
      try {
        await fetchPaymentMethods();
        await fetchUsageStats();
      } catch (err) {
        // Errors handled in the API functions
      }
    };
    
    loadData();
  }, []);

  // Update useExistingCard when payment methods change
  useEffect(() => {
    setUseExistingCard(paymentMethods.length > 0);
  }, [paymentMethods]);

  // Function to estimate tax based on amount
  const handleTaxEstimation = async (amountToEstimate: number, country: string = selectedCountry) => {
    if (amountToEstimate < 10) {
      setTaxEstimate(0);
      setTotalWithTax(amountToEstimate);
      setTaxRate(0);
      return;
    }

    setEstimatingTax(true);
    console.log(`Estimating tax for ${amountToEstimate} in ${country}`);
    
    try {
      let taxResult;
      
      if (country === 'IL') {
        // Try Israel first
        taxResult = await estimateTax(amountToEstimate, 'IL');
        console.log('Israel tax result:', taxResult);
        
        // If Israel returns no tax or fails, note it but don't switch
        if (!taxResult.success || taxResult.tax === 0) {
          console.log('Israel: No tax applicable for digital services');
        }
      } else {
        // Use selected country
        const state = country === 'US' ? 'CA' : undefined; // California for US demo
        taxResult = await estimateTax(amountToEstimate, country, state);
        console.log(`${country} tax result:`, taxResult);
      }
      
      if (taxResult && taxResult.success) {
        setTaxEstimate(taxResult.tax);
        setTotalWithTax(taxResult.total);
        setTaxType(taxResult.taxType);
        setTaxRate(taxResult.taxRate);
        
        if (taxResult.tax === 0) {
          console.log(`No tax applicable in ${country}`);
        }
      } else {
        // Fallback to no tax if estimation fails
        console.warn('Tax estimation failed, using no tax');
        setTaxEstimate(0);
        setTotalWithTax(amountToEstimate);
        setTaxRate(0);
      }
    } catch (error) {
      console.error('Tax estimation failed:', error);
      setTaxEstimate(0);
      setTotalWithTax(amountToEstimate);
      setTaxRate(0);
    } finally {
      setEstimatingTax(false);
    }
  };

  // Handle amount change from slider
  const handleAmountChange = (_event: Event, newValue: number | number[]) => {
    const value = newValue as number;
    setAmount(value);
    setCustomAmount(value.toString());
    handleTaxEstimation(value, selectedCountry);
  };

  // Handle custom amount input
  const handleCustomAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    
    if (!value) {
      setCustomAmount('');
      setAmount(0);
      return;
    }
    
    // Only allow valid numbers
    if (/^\d*\.?\d{0,2}$/.test(value) && parseFloat(value) <= 1000) {
      setCustomAmount(value);
      const newAmount = parseFloat(value) || 0;
      setAmount(newAmount);
      handleTaxEstimation(newAmount, selectedCountry);
    }
  };

  // Handle country selection change
  const handleCountryChange = (event: any) => {
    const newCountry = event.target.value;
    setSelectedCountry(newCountry);
    handleTaxEstimation(amount, newCountry);
  };

  // Handle payment method change
  const handlePaymentMethodChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUseExistingCard(event.target.value === 'existing');
    setCardError(null);
  };

  // Initialize payment
  const initializePayment = async () => {
    if (amount < 10) {
      setPaymentError('Minimum payment amount is $10');
      return;
    }
    
    setProcessing(true);
    setPaymentError(null);
    
    try {
      // Create a payment intent on the server
      const secret = await createPaymentIntent(amount);
      setClientSecret(secret);
    } catch (error: any) {
      setPaymentError(error.message || 'Failed to initiate payment. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  // Handle form submission
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!stripe || !clientSecret) {
      return;
    }
    
    setProcessing(true);
    setPaymentError(null);
    
    try {
      let paymentResult;
      
      if (useExistingCard && paymentMethods.length > 0) {
        // Use existing payment method
        const defaultMethod = paymentMethods.find(m => m.isDefault) || paymentMethods[0];
        
        paymentResult = await stripe.confirmCardPayment(clientSecret, {
          payment_method: defaultMethod.id,
        });
      } else {
        // Use new card
        const cardElement = elements?.getElement(CardElement);
        
        if (!cardElement) {
          setPaymentError('Card information is missing');
          setProcessing(false);
          return;
        }
        
        paymentResult = await stripe.confirmCardPayment(clientSecret, {
          payment_method: {
            card: cardElement,
          },
        });
      }
      
      if (paymentResult.error) {
        setPaymentError(paymentResult.error.message || 'Payment failed. Please try again.');
      } else if (paymentResult.paymentIntent.status === 'succeeded') {
        setPaymentSuccess(true);
        // Refresh balance
        await fetchUsageStats();
      }
    } catch (error: any) {
      setPaymentError(error.message || 'Payment failed. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  // Select a predefined amount
  const handleSelectAmount = (value: number) => {
    setAmount(value);
    setCustomAmount(value.toString());
    handleTaxEstimation(value, selectedCountry);
  };

  // Reset payment after success to allow another payment
  const handleResetPayment = () => {
    setPaymentSuccess(false);
    setClientSecret(null);
    setAmount(25);
    setCustomAmount('25');
    setTaxEstimate(0);
    setTotalWithTax(25);
    setTaxRate(0);
    handleTaxEstimation(25, selectedCountry);
  };

  // Initialize tax estimation on component mount
  useEffect(() => {
    handleTaxEstimation(25, selectedCountry);
  }, []);

  return (
    <Grid container spacing={4}>
      <Grid item xs={12} md={7}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h5" gutterBottom>
            Add Credits to Your Account
          </Typography>
          
          <Divider sx={{ mb: 3 }} />
          
          {paymentSuccess ? (
            <Box textAlign="center" py={4}>
              <Alert severity="success" sx={{ mb: 3 }}>
                Your payment was successful! Your balance has been updated.
              </Alert>
              
              <Typography variant="h6" gutterBottom>
                New Balance: ${usage?.balance.toFixed(2) || '0.00'}
              </Typography>
              
              <Button
                variant="contained"
                color="primary"
                onClick={handleResetPayment}
                sx={{ mt: 2 }}
              >
                Make Another Payment
              </Button>
            </Box>
          ) : clientSecret ? (
            // Payment confirmation form
            <Box component="form" onSubmit={handleSubmit} noValidate>
              <Typography variant="h6" gutterBottom>
                Confirm Payment of ${amount.toFixed(2)}
              </Typography>
              
              {paymentError && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {paymentError}
                </Alert>
              )}
              
              {!useExistingCard && (
                <Box sx={{ mb: 3, mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Enter Card Information
                  </Typography>
                  <Box
                    sx={{
                      p: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                    }}
                  >
                    <CardElement options={cardElementOptions} />
                  </Box>
                </Box>
              )}
              
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
                <Button
                  onClick={() => setClientSecret(null)}
                  disabled={processing}
                >
                  Back
                </Button>
                
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={!stripe || processing}
                >
                  {processing ? <CircularProgress size={24} /> : 'Confirm Payment'}
                </Button>
              </Box>
            </Box>
          ) : (
            // Payment amount selection form
            <Box>
              {paymentError && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {paymentError}
                </Alert>
              )}
              
              <Typography variant="subtitle1" gutterBottom>
                Current Balance: ${usage?.balance.toFixed(2) || '0.00'}
              </Typography>
              
              <Typography variant="subtitle1" gutterBottom sx={{ mt: 3 }}>
                Select Amount to Add
              </Typography>
              
              {/* Country Selection */}
              <Box sx={{ mb: 3 }}>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>Tax Location:</Typography>
                  <Select
                    value={selectedCountry}
                    onChange={handleCountryChange}
                    displayEmpty
                  >
                    <MenuItem value="IL">🇮🇱 Israel</MenuItem>
                    <MenuItem value="US">🇺🇸 United States</MenuItem>
                    <MenuItem value="GB">🇬🇧 United Kingdom</MenuItem>
                    <MenuItem value="DE">🇩🇪 Germany</MenuItem>
                    <MenuItem value="FR">🇫🇷 France</MenuItem>
                    <MenuItem value="CA">🇨🇦 Canada</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              
              <Box sx={{ mb: 4, mt: 2 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                  {predefinedAmounts.map((value) => (
                    <Chip
                      key={value}
                      label={`$${value}`}
                      onClick={() => handleSelectAmount(value)}
                      color={amount === value ? 'primary' : 'default'}
                      sx={{ px: 1 }}
                    />
                  ))}
                </Box>
                
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs>
                    <Slider
                      value={amount}
                      onChange={handleAmountChange}
                      min={10}
                      max={500}
                      step={5}
                      aria-labelledby="payment-amount-slider"
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      value={customAmount}
                      onChange={handleCustomAmountChange}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      }}
                      inputProps={{
                        step: 1,
                        min: 10,
                        max: 1000,
                        type: 'number',
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>
              
              {paymentMethods.length > 0 && (
                <Box sx={{ mb: 4 }}>
                  <FormControl component="fieldset">
                    <FormLabel component="legend">Payment Method</FormLabel>
                    <RadioGroup
                      name="payment-method"
                      value={useExistingCard ? 'existing' : 'new'}
                      onChange={handlePaymentMethodChange}
                    >
                      <FormControlLabel 
                        value="existing" 
                        control={<Radio />} 
                        label={
                          <Box>
                            <Typography variant="body1">
                              Use default payment method
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {paymentMethods.find(m => m.isDefault) ? 
                                `${paymentMethods.find(m => m.isDefault)?.brand} •••• ${paymentMethods.find(m => m.isDefault)?.last4}` : 
                                `${paymentMethods[0].brand} •••• ${paymentMethods[0].last4}`}
                            </Typography>
                          </Box>
                        } 
                      />
                      <FormControlLabel 
                        value="new" 
                        control={<Radio />}
                        label="Use a new card" 
                      />
                    </RadioGroup>
                  </FormControl>
                </Box>
              )}
              
              {/* Tax Summary */}
              <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Payment Summary
                </Typography>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Amount:</Typography>
                  <Typography variant="body2">${amount.toFixed(2)}</Typography>
                </Box>
                
                {estimatingTax ? (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Estimating tax...</Typography>
                    <CircularProgress size={16} />
                  </Box>
                ) : taxEstimate > 0 ? (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">
                      {taxType === 'sales_tax' ? 'Sales Tax' : 
                       taxType === 'vat' ? 'VAT' : 
                       taxType === 'gst' ? 'GST' : 'Tax'}
                      {taxRate > 0 && ` (${taxRate.toFixed(2)}%)`}:
                    </Typography>
                    <Typography variant="body2">${taxEstimate.toFixed(2)}</Typography>
                  </Box>
                ) : null}
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', borderTop: 1, borderColor: 'divider', pt: 1 }}>
                  <Typography variant="subtitle2" fontWeight="bold">Total:</Typography>
                  <Typography variant="subtitle2" fontWeight="bold">
                    ${totalWithTax.toFixed(2)}
                  </Typography>
                </Box>
              </Box>

              <Button
                fullWidth
                variant="contained"
                color="primary"
                onClick={initializePayment}
                disabled={amount < 10 || processing || estimatingTax}
              >
                {processing ? <CircularProgress size={24} /> : 
                 estimatingTax ? 'Calculating...' :
                 `Pay ${totalWithTax.toFixed(2)}`}
              </Button>
              
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, textAlign: 'center' }}>
                Payments are securely processed through Stripe. Your card information is never stored on our servers.
              </Typography>
            </Box>
          )}
        </Paper>
      </Grid>
      
      <Grid item xs={12} md={5}>
        <PaymentMethodsList />
        
        <Paper sx={{ p: 3, mt: 4 }}>
          <Typography variant="h6" gutterBottom>
            Credit Usage
          </Typography>
          
          <Divider sx={{ mb: 3 }} />
          
          <Typography variant="body2" gutterBottom>
          Token credits are used per request based on model complexity and capability.
            {`Use '${brand.storagePrefix} models' command to see current pricing for each AI model.`}
            </Typography>
          
          <Box sx={{ mt: 2, mb: 2 }}>
            <Typography variant="body2">
              • Different models have different pricing based on their capabilities
            </Typography>
            <Typography variant="body2">
              {`\u2022 Use '${brand.storagePrefix} models' to see current rates for each AI model`}
            </Typography>
          </Box>
          
          <Typography variant="body2" sx={{ mt: 2 }}>
            Your current balance: ${usage?.balance.toFixed(2) || '0.00'}
          </Typography>
        </Paper>
      </Grid>
    </Grid>
  );
};

// ============================================================================
// INVITATION-ONLY MODE COMPONENT
// ============================================================================
const InvitationOnlyContent: React.FC = () => {
  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Paper
        sx={{
          p: { xs: 4, md: 6 },
          textAlign: 'center',
          background: brand.gradients.hero,
          color: 'white',
          borderRadius: 3,
        }}
      >
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            bgcolor: 'rgba(255,255,255,0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 3,
          }}
        >
          <LockIcon sx={{ fontSize: 40 }} />
        </Box>

        <Typography
          variant="h4"
          component="h2"
          fontWeight="bold"
          gutterBottom
          sx={{ fontSize: { xs: '1.75rem', md: '2.125rem' } }}
        >
          Invitation Only
        </Typography>

        <Typography
          variant="h6"
          sx={{
            opacity: 0.9,
            maxWidth: 500,
            mx: 'auto',
            mb: 4,
            lineHeight: 1.6,
            fontWeight: 400,
          }}
        >
          {brand.fullProductName} is currently running in early access mode.
          We're onboarding select users to ensure the best experience.
        </Typography>

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 3,
            justifyContent: 'center',
            mb: 4,
          }}
        >
          <Box
            sx={{
              bgcolor: 'rgba(255,255,255,0.15)',
              borderRadius: 2,
              p: 3,
              flex: 1,
              maxWidth: 280,
            }}
          >
            <GroupAddIcon sx={{ fontSize: 32, mb: 1 }} />
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Request Early Access
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Interested in joining early? Send us your request and we'll get back to you.
            </Typography>
          </Box>

          <Box
            sx={{
              bgcolor: 'rgba(255,255,255,0.15)',
              borderRadius: 2,
              p: 3,
              flex: 1,
              maxWidth: 280,
            }}
          >
            <CalendarIcon sx={{ fontSize: 32, mb: 1 }} />
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Public Launch
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Expected to open for all paying customers by March 2026.
            </Typography>
          </Box>
        </Box>

        <Button
          variant="contained"
          size="large"
          href={`mailto:${brand.contactEmail}?subject=Early Access Request`}
          startIcon={<EmailIcon />}
          sx={{
            bgcolor: 'white',
            color: brand.colors.fire,
            px: 4,
            py: 1.5,
            fontWeight: 600,
            '&:hover': {
              bgcolor: 'rgba(255,255,255,0.9)',
            },
          }}
        >
          Contact Us for Access
        </Button>

        <Typography
          variant="body2"
          sx={{ mt: 3, opacity: 0.8 }}
        >
          {brand.contactEmail}
        </Typography>
      </Paper>

      <Paper sx={{ p: 3, mt: 4 }}>
        <Typography variant="h6" gutterBottom>
          Why Invitation Only?
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          We're committed to delivering the highest quality AI-powered development experience.
          By limiting access during this phase, we can:
        </Typography>
        <Box component="ul" sx={{ pl: 2, color: 'text.secondary' }}>
          <Typography component="li" variant="body2" sx={{ mb: 1 }}>
            Provide personalized onboarding and support to early users
          </Typography>
          <Typography component="li" variant="body2" sx={{ mb: 1 }}>
            Gather valuable feedback to improve the platform
          </Typography>
          <Typography component="li" variant="body2" sx={{ mb: 1 }}>
            Ensure system stability and performance at scale
          </Typography>
          <Typography component="li" variant="body2">
            Build a community of engaged early adopters
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

// ============================================================================
// MAIN COMPONENT - Renders based on INVITATION_MODE_ENABLED toggle
// ============================================================================
const PaymentPage: React.FC = () => {
  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Billing & Credits
      </Typography>

      {INVITATION_MODE_ENABLED ? (
        <InvitationOnlyContent />
      ) : (
        <Grid container spacing={4}>
          <Grid item xs={12} md={7}>
            <SubscriptionManagement />
          </Grid>
          <Grid item xs={12} md={5}>
            <PaymentMethodsList />
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default PaymentPage;
